<script setup lang="ts">
import store from "@/store";
import { BeruCardFree, BeruCardTaken } from "@/types/beru";
import { copyText } from "@/utils/main";
import { addHours, format } from "date-fns";
import ru from "date-fns/locale/ru";
import { computed } from "vue";
import { POSITION, useToast } from "vue-toastification";

interface Props {
  card: BeruCardFree | BeruCardTaken;
}

const props = defineProps<Props>();
const toast = useToast();

const isCardExchangeFree = (card: BeruCardFree | BeruCardTaken): card is BeruCardFree =>
  'startTime' in card;

const startTime = computed(() =>
  isCardExchangeFree(props.card) ? props.card.startTime : props.card.dateFrom
);
const endTime = computed(() =>
  isCardExchangeFree(props.card) ? props.card.endTime : props.card.dateTo
);

const onCopyClick = (value: string | number | null) => {
  copyText(value);
  toast.success("Сохранено в буфер обмена", {
    position: POSITION.BOTTOM_RIGHT
  });
};

const pharmTz = computed<number>(() => {
  return store.getters["data/pharmTz"] || 0;
});

// Получаем дату и время с учётом смещения таймзоны аптеки
const toPharmDate = (iso: string): Date => {
  const timeZone = pharmTz.value + new Date().getTimezoneOffset() / 60;
  return addHours(new Date(iso), timeZone);
};

const formatDateTime = (iso: string): string => format(toPharmDate(iso), 'd MMMM yyyy', { locale: ru });

const formatTime = (iso: string): string => format(toPharmDate(iso), "HH:mm");
</script>

<template>
  <div class="beru-card" :class="{'is-canceled': card.armZortCanceledAt}">
    <div class="beru-card-badges">
      <div class="beru-card-badge success" v-if="card.shiftCost" data-qa="beru-card-badge-cost">
        +{{ card.shiftCost }}
      </div>
      <div class="beru-card-badge success" v-if="card.shiftCostRD" data-qa="beru-card-badge-cost-rd">
        ~РД {{ card.shiftCostRD }}
      </div>
      <div
        class="beru-card-badge primary"
        @click.stop="onCopyClick(card.serialNumber)"
        v-if="card.serialNumber"
        data-qa="beru-card-badge-copy-number"
      >
        <span>№ {{ card.serialNumber }}</span>
        <button class="copy-btn" type="button" data-qa="beru-card-button-copy-number">
          <vita-icon name="copy" />
        </button>
      </div>
    </div>
    <div class="beru-card-row">
      <div>
        <div class="beru-card-title" v-if="startTime" data-qa="beru-card-value-date">
          {{ formatDateTime(startTime) }}
        </div>
        <div class="beru-card-interval" v-if="startTime && endTime" data-qa="beru-card-value-interval">
          {{ formatTime(startTime) }} - {{ formatTime(endTime) }}
        </div>
      </div>
      <div v-if="card.armZortCanceledAt">
        <div class="beru-card-status" data-qa="beru-card-status-canceled">Отменена</div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.beru-card {
  background-color: #fff;
  color: rgba(51, 51, 51, 1);
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.1);
  padding: 16px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: color 0.15s ease;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    box-shadow: 0 3px 16px 0 rgba(0, 0, 0, 0.08);
  }

  &.is-canceled {
    color: rgba(117, 117, 117, 1);
  }

  &-badges {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
  }

  &-badge {
    padding: 4px 6px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0;
    vertical-align: middle;
    background-color: rgba(238, 248, 255, 1);
    color: rgba(51, 51, 51, 1);

    &.success {
      background-color: #30b648;
      color: #ffffff;
    }
  }

  &-title {
    font-weight: 600;
    font-size: 15px;
    line-height: 100%;
    letter-spacing: 0;
    vertical-align: middle;
    color: inherit;
  }

  &-date {
    color: inherit;
  }

  &-status {
    display: inline-flex;
    background-color: rgba(203, 207, 210, 0.2);
    color: rgba(52, 59, 69, 0.6);
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    letter-spacing: 0;
    vertical-align: bottom;
    border-radius: 12px;
    padding: 5px 8px;
  }

  &-row {
    display: flex;
    justify-content: space-between;
  }
}

.copy-btn {
  border: 0;
  outline: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.15s ease;
  background: transparent;
  width: 16px;
  min-width: 16px;
  height: 16px;

  svg {
    width: 17px;
    height: 17px;
    fill: #30a8ff;
  }

  &:hover {
    opacity: 0.7;
  }
}
</style>
